import * as React from 'react';
import { withStyles } from '../../settings/styles';
import { connect } from 'react-redux';
import { AppState } from '../../model/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { loadingOverlay, broadcastMessage } from '../../model/overlay/actions';
import { BroadcastType } from '../../model/overlay/types';
import { Delete, Edit, Add } from '@mui/icons-material';
import { IServiceState } from '../../model/services/types';
import { addService, editService, removeService, AddServiceType, EditServiceType, RemoveServiceType } from '../../model/services/actions';
import { UserState } from '../../model/user/types';
import { NavigationTabProps } from '../../model/navigation/types';
import { withTranslation } from 'react-i18next';
import { ServiceSettings } from '../../settings/services';
import { vsprintf } from 'sprintf-js';
import { AppIcons } from '../../settings/icons';
import CreateEditDialog from '../../components/dialogs/CreateEditDialog';
import DeleteDialog from '../../components/dialogs/DeleteDialog';
import ResultDialog from '../../components/dialogs/ResultDialog';
import { PropertyType } from '../../types/PropertyType';
import PropertyList from '../../components/display/PropertyList';
import SelectableItemList from '../../components/display/SelectableItemList';
import { Box, Typography } from '@mui/material';

// -----------------------------------------------  //
// ---------------- State & Props ----------------  //
// -----------------------------------------------  //

interface State 
{
    createEditDialog: {
        open: boolean;
        handle: number;
    }
    deleteDialog: {
        open: boolean;
        handle: number;
    }
    resultDialog: {
        open: boolean;
        handle: number;
    }
};

interface StateProps
{
    user: UserState;
    services: IServiceState[];
}

interface DispatchProps
{
    loading: ()=>void;
    doneLoading: ()=>void;
    broadcast: (msg: string, type?: BroadcastType) => void;
    create: AddServiceType;
    edit: EditServiceType;
    remove: RemoveServiceType;
}

type Props = NavigationTabProps & StateProps & DispatchProps;

// -----------------------------------------------  //
// -------------------- Class --------------------  //
// -----------------------------------------------  //

class Services extends React.Component<Props, State> 
{
    constructor(props: any)
    {
        super(props);

        // Init State
        this.state = {
            createEditDialog: {
                open: false,
                handle: -1
            },
            deleteDialog: {
                open: false,
                handle: -1
            },
            resultDialog: {
                open: false,
                handle: -1
            }
        };

        // Setup Member Functions
        this.actionCreate = this.actionCreate.bind(this);
        this.actionEdit = this.actionEdit.bind(this);
        this.actionDelete = this.actionDelete.bind(this);

        this.dialogCreateEditOpen = this.dialogCreateEditOpen.bind(this);
        this.dialogCreateEditClose = this.dialogCreateEditClose.bind(this);
        this.dialogDeleteOpen = this.dialogDeleteOpen.bind(this);
        this.dialogDeleteClose = this.dialogDeleteClose.bind(this);
        this.dialogResultOpen = this.dialogResultOpen.bind(this);
        this.dialogResultClose = this.dialogResultClose.bind(this);
    }

    // ============================================ //
    //               Render Functions               //
    // ============================================ //

    render () 
    {
        return (<>
            {this.renderTitle()}
            {this.renderServicesList()}
            {this.renderDialogs()}
        </>);
    }

    renderTitle()
    {
        const { t } = this.props;
        return (<Box sx={{ maxWidth: 936, margin: 'auto', overflow: 'hidden' }}>
            <Typography sx={{mb: "30px", ml: 1, fontSize: 18, fontWeight: 5}}>
                {t('services.myServices')}
            </Typography>
        </Box>);
    }

    renderServicesList()
    {
        const { t } = this.props;
        const servicesAsListItems = this.props.services.map(service=>{
            let setting = ServiceSettings.getSettings(service.serviceType);
            return {
                title: service.name,
                icon: setting?.icon,
                typeTitle: setting?.title
            }
        });

        return (<SelectableItemList 
            typeName={t('services.service')}
            typeNamePlural={t('services.services')}
            items={servicesAsListItems}
            onItemClick={this.dialogResultOpen}
            actions={[{
                title: `${t('general.add')} ${t('services.service')}`,
                icon: Add,
                onAction: this.dialogCreateEditOpen
            }]}
        />);
    }

    renderDialogs()
    {
        return (<>
            {this.renderCreateDialog()}
            {this.renderEditDialog()}
            {this.renderDeleteDialog()}
            {this.renderViewServiceDialog()}
        </>)
    }

    renderCreateDialog()
    {
        if(this.state.createEditDialog.handle >= 0) return;
        const { t } = this.props;
        const serviceList = Object.values(ServiceSettings.services);

        const createData = {
            id: "createService",
            title: t('services.service'),
            icon: AppIcons.Services,
            properties: [
                {
                    id: "name",
                    title: t('general.name'),
                    type: PropertyType.NAME
                },
                {
                    id: "service",
                    title: t('services.services'),
                    objects: serviceList.map(service=>({
                        id: service.id,
                        title: service.title,
                        icon: service.icon,
                        properties: service.properties
                    } as IObject))
                } as IObjects
            ]
        } as IObject;

        return (<CreateEditDialog 
            open={this.state.createEditDialog.open} 
            object={createData}
            onClose={this.dialogCreateEditClose} 
            onCreate={this.actionCreate}
        />);
    }

    renderEditDialog()
    {
        if(this.state.createEditDialog.handle < 0) return;
        const { t } = this.props;
        const editService = this.props.services[this.state.createEditDialog.handle];
        const editSettings = ServiceSettings.getSettings(editService.serviceType);

        const createEditData = {
            id: editService.id,
            title: editService.name,
            icon: AppIcons.Services,
            properties: [
                {
                    id: "name",
                    title: t('general.name'),
                    type: PropertyType.NAME,
                    value: editService.name
                } as IProperty,
                {
                    id: "service",
                    title: t('services.services'),
                    objects: [{
                        id: editService.serviceType,
                        title: editSettings?.title,
                        icon: editSettings?.icon,
                        properties: editService.properties
                    }]
                } as IObjects
            ]
        } as IObject;

        return (<CreateEditDialog 
            open={this.state.createEditDialog.open} 
            object={createEditData}
            editing={true}
            onClose={this.dialogCreateEditClose}
            onEdit={this.actionEdit}
        />);
    }

    renderDeleteDialog()
    {
        if(this.state.deleteDialog.handle < 0) return;
        const service = this.props.services[this.state.deleteDialog.handle];
        const settings = ServiceSettings.getSettings(service.serviceType);

        return (<DeleteDialog
            open={this.state.deleteDialog.open}
            typeTitle={settings?.title}
            itemTitle={service.name}
            onClose={this.dialogDeleteClose}
            onDelete={this.actionDelete}
        />)
    }

    renderViewServiceDialog()
    {
        if(this.state.resultDialog.handle < 0) return;
        const { t } = this.props;
        const service = this.props.services[this.state.resultDialog.handle];
        const settings = ServiceSettings.getSettings(service.serviceType);

        return (<ResultDialog
            title={`${service.name} - ${settings?.title} ${t('services.service')}`}
            icon={settings?.icon}
            open={this.state.resultDialog.open}
            onClose={this.dialogResultClose}
            actions={[
                {
                    id: service.id,
                    title: `${t('general.delete')}`,
                    icon: Delete,
                    secondaryColor: true,
                    onAction: (id: string)=>{this.dialogDeleteOpen(id)}
                },
                {
                    id: service.id,
                    title: `${t('general.edit')}`,
                    icon: Edit,
                    onAction: (id: string)=>{this.dialogCreateEditOpen(this.props.services.findIndex(s=>s.id===id))}
                }
            ]}
        >
            <PropertyList 
                properties={[
                    {
                        id: "serviceUrl",
                        title: `${t('services.service')} URL`,
                        type: PropertyType.LINK,
                        value: `${window.location.origin}/api/service/${service.userId}/${service.id}`
                    } as IProperty,
                    ...service.properties
                ]} 
                onCopyValue={(succeded)=>{
                    if(succeded)
                    {
                        this.props.broadcast(`${t('general.copiedToClipboard')}`, BroadcastType.INFO)
                        return;
                    }
                    this.props.broadcast(`${t('general.copiedToClipboardFailed')}`, BroadcastType.ERROR)
                }} 
            />
        </ResultDialog>)
    }

    // ============================================ //
    //               Action Functions               //
    // ============================================ //

    actionCreate(jsonData: any)
    {
        const { t } = this.props;
        const name = jsonData.properties.name;
        const {id, ...params} = jsonData.properties.service;
        this.dialogCreateEditClose();
        this.props.loading();
        this.props.create(
            name, 
            id,
            params
        ).then((state: IServiceState) => {
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('services.added'), [state.name]), BroadcastType.SUCCESS);
            this.dialogResultOpen(this.props.services.findIndex(s=>s.id===state.id));
        }).catch((err) => {
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('services.addFailed'), [name]), BroadcastType.ERROR);
        });
    }

    actionEdit(jsonData: any)
    {
        const { t } = this.props;
        this.props.broadcast(t('general.notImplemented'), BroadcastType.WARNING);
        this.dialogCreateEditClose();
    }

    actionDelete()
    {
        const { t } = this.props;
        const service = this.props.services[this.state.deleteDialog.handle];
        if(service === undefined) return;
        
        this.dialogDeleteClose();
        this.dialogResultClose();
        this.props.loading();
        this.props.remove(service.id).then(() => {
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('services.removed'), [service.name]), BroadcastType.SUCCESS);
        }).catch(err=>{
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('services.removeFailed'), [service.name]), BroadcastType.ERROR);
        });
    }

    // ============================================ //
    //               Dialog Functions               //
    // ============================================ //

    dialogCreateEditOpen(handle: number = -1)
    {
        this.setState({ createEditDialog: { open: true, handle: handle } });
    }

    dialogCreateEditClose()
    {
        this.setState({ createEditDialog: { open: false, handle: -1 } });
    }

    dialogResultOpen(handle: number)
    {
        this.setState({ resultDialog: { open: true, handle: handle } });
    }

    dialogResultClose()
    {
        this.setState({ resultDialog: { open: false, handle: -1 } });
    }

    dialogDeleteOpen(id: string)
    {
        this.setState({ deleteDialog: { open: true, handle: this.props.services.findIndex(s=>s.id===id) } });
    }

    dialogDeleteClose()
    {
        this.setState({ deleteDialog: { open: false, handle: -1 } });
    }
}

// ----------------- Export -----------------  //

const mapStateToProps = (state: AppState, props: NavigationTabProps): StateProps => ({
    user: state.user.current,
    services: state.services.myActiveServices
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{},{},any>, props: NavigationTabProps): DispatchProps => ({
    loading: ()=>dispatch(loadingOverlay(true)),
    doneLoading: ()=>dispatch(loadingOverlay(false)),
    broadcast: (msg, type?)=>dispatch(broadcastMessage(msg, type)),
    create: (name: string, type: string, props: object)=>addService(name, type, props)(dispatch),
    edit: (id: string, name: string,  props: object)=>editService(id, name, props)(dispatch),
    remove: (id: string)=>removeService(id)(dispatch),
});

export default withStyles(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Services)));

// ------------------------------------------  //